import * as Sentry from '@sentry/react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { cn } from 'src/helpers/style'
import i18n from 'src/i18n'

import {
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import {
  Header,
  headerBrandLinkClassName,
  headerNavTabClassName,
} from 'src/components/molecules'

import { useSessionRestore } from 'src/app/auth/hooks/use-session-restore'
import { useAuthStore, useSignoutMutation } from 'src/app/auth/interfaces'
import { useHanwhaAccessQuery } from 'src/app/auth/repositories/use-hanwha-access-query'
import { Toast } from 'src/app/core/services/toast'

// Vault is backed by the Hanwha staging API, so organization administration
// must remain on the Studio deployment connected to the same tenant data.
// Access protection for that deployment is handled separately from routing.
const organizationManagementUrl =
  'https://stg.studio.knapsack.ai/admin/organizations'

const mobileNavItemClassName = (isActive: boolean): string =>
  cn(
    'block border-b border-border-default py-5 no-underline transition-colors border-l-2 pl-[18px] pr-5',
    isActive ? 'border-l-content-primary' : 'border-l-transparent',
  )

export function Layout(): JSX.Element {
  useSessionRestore()
  const { isAuthenticated } = useAuthStore()
  const uuid = useAuthStore(state => state.currentUser?.data.uuid ?? null)
  const isSuperAdmin = useAuthStore(
    state => state.currentUser?.data.isSuperAdmin === true,
  )
  const accessQuery = useHanwhaAccessQuery({
    uuid,
    enabled: isAuthenticated() && !isSuperAdmin,
  })
  const isOrganizationAdmin = isSuperAdmin || accessQuery.data?.isAdmin === true
  const navigate = useNavigate()
  const { mutateAsync: signout } = useSignoutMutation()

  return (
    <Header
      isAuthenticated={isAuthenticated}
      brand={
        <NavLink to="/" className={headerBrandLinkClassName}>
          <Typography
            variant={TypographyVariant.titleSm}
            weight={TypographyWeight.medium}
            className="whitespace-nowrap text-brand-accent"
          >
            {i18n.t('brand.name')}
          </Typography>
        </NavLink>
      }
      onSignOut={async () => {
        try {
          await signout()
        } catch (error) {
          // The local mutation cleanup has completed, but the HttpOnly refresh
          // cookie may remain usable until a later server-confirmed sign-out.
          // Do not present this recovery path as a complete server logout.
          Sentry.withScope(scope => {
            scope.setTag('error.source', 'logout')
            scope.setLevel('warning')
            Sentry.captureException(error)
          })
          Toast.notify({
            title: i18n.t('auth.logout.unconfirmedTitle'),
            message: i18n.t('auth.logout.unconfirmedDescription'),
            type: Toast.toastType.WARNING,
          })
        } finally {
          navigate('/')
        }
      }}
      links={
        <nav
          className="flex flex-row flex-wrap items-center justify-center gap-4"
          aria-label="Main"
        >
          <NavLink
            to="/fund-updates"
            className={({ isActive }) => headerNavTabClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.labelMd}
              weight={TypographyWeight.light}
              className="!text-inherit"
            >
              {i18n.t('header.fundUpdates')}
            </Typography>
          </NavLink>
          {isOrganizationAdmin && (
            <a
              href={organizationManagementUrl}
              className={headerNavTabClassName(false)}
            >
              <Typography
                variant={TypographyVariant.labelMd}
                weight={TypographyWeight.light}
                className="!text-inherit"
              >
                {i18n.t('header.organizationManagement')}
              </Typography>
            </a>
          )}
          <NavLink
            to="/why-we-invest"
            className={({ isActive }) => headerNavTabClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.labelMd}
              weight={TypographyWeight.light}
              className="!text-inherit"
            >
              {i18n.t('header.whyWeInvest')}
            </Typography>
          </NavLink>
          <NavLink
            to="/portfolio-news"
            className={({ isActive }) => headerNavTabClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.labelMd}
              weight={TypographyWeight.light}
              className="!text-inherit"
            >
              {i18n.t('header.portfolioNews')}
            </Typography>
          </NavLink>
          <NavLink
            to="/member-insights"
            className={({ isActive }) => headerNavTabClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.labelMd}
              weight={TypographyWeight.light}
              className="!text-inherit"
            >
              {i18n.t('header.memberInsights')}
            </Typography>
          </NavLink>
        </nav>
      }
      mobileMenuLinks={onClose => (
        <>
          <NavLink
            to="/fund-updates"
            onClick={onClose}
            className={({ isActive }) => mobileNavItemClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.light}
            >
              {i18n.t('header.fundUpdates')}
            </Typography>
          </NavLink>
          <NavLink
            to="/why-we-invest"
            onClick={onClose}
            className={({ isActive }) => mobileNavItemClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.light}
            >
              {i18n.t('header.whyWeInvest')}
            </Typography>
          </NavLink>
          <NavLink
            to="/portfolio-news"
            onClick={onClose}
            className={({ isActive }) => mobileNavItemClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.light}
            >
              {i18n.t('header.portfolioNews')}
            </Typography>
          </NavLink>
          <NavLink
            to="/member-insights"
            onClick={onClose}
            className={({ isActive }) => mobileNavItemClassName(isActive)}
          >
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.light}
            >
              {i18n.t('header.memberInsights')}
            </Typography>
          </NavLink>
          {isOrganizationAdmin && (
            <a
              href={organizationManagementUrl}
              onClick={onClose}
              className={mobileNavItemClassName(false)}
            >
              <Typography
                variant={TypographyVariant.titleSm}
                weight={TypographyWeight.light}
              >
                {i18n.t('header.organizationManagement')}
              </Typography>
            </a>
          )}
        </>
      )}
    >
      <Outlet />
    </Header>
  )
}
